// components/Calendar/views/WeekView.jsx
import React from 'react';
import { Plus, User } from 'lucide-react';
import { AppointmentServiceUtils } from '../../../utils/appointmentUtils';
import { getAppointmentsForDay, getAppointmentTypeClass, getHolidaysForDay, sortAppointmentsByTime } from '../../../utils/appointmentUtils';
import { isToday as isTodayUtil, formatTime } from '../../../utils/dateUtils';

const WeekView = ({
  viewDays,
  appointments,
  holidays,
  selectedStylist,
  onDayClick,
  onAppointmentClick,
  highlightedAppointment,
  onDragStart,
  onDragEnd,
  useMilitaryTime = false
}) => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter appointments based on mobile stylist selection
 const filteredAppointments = React.useMemo(() => {
  // Remove the isMobile check - filter on all screen sizes when stylist is selected
  if (!selectedStylist) {
    return appointments; // Show all appointments when no stylist selected
  }
  
  // Filter appointments for the selected stylist (works on both mobile and desktop)
  return appointments.filter(apt => {
    const matchesById = apt.MWuserID === selectedStylist.id;
    const matchesByName = apt.MeetingWith === selectedStylist.name;
    return matchesById || matchesByName;
  });
}, [appointments, selectedStylist]); // Remove isMobile from dependencies

  // Debug logging
  React.useEffect(() => {
    console.log('WeekView filtering:', {
      isMobile,
      selectedStylist: selectedStylist?.name,
      totalAppointments: appointments.length,
      filteredAppointments: filteredAppointments.length
    });
  }, [isMobile, selectedStylist, appointments.length, filteredAppointments.length]);

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetDay) => {
    e.preventDefault();
    // The actual drop logic is handled in the parent component
    // We just need to call the parent's drop handler with the target date
    if (onDragEnd) {
      onDragEnd(e, targetDay);
    }
  };

  return (
    <div className="week-overview">
      {/* Mobile message when no stylist selected */}
      {isMobile && !selectedStylist && (
        <div className="mobile-no-stylist-message">
          <div className="no-stylist-prompt">
            <User className="w-8 h-8 mb-2 text-gray-400" />
            <p className="text-lg font-medium text-gray-600">Select a stylist to view their schedule</p>
            <p className="text-sm text-gray-500 mt-1">Use the dropdown above to choose a stylist</p>
          </div>
        </div>
      )}

      {/* Show week grid when: Desktop (always) OR Mobile with selected stylist */}
      {(!isMobile || selectedStylist) && (
        <div className="week-grid">
          {/* Mobile: Show selected stylist info */}
          

          <div className="week-days-header">
            {viewDays.map((day, index) => {
              const isToday = isTodayUtil(day);
              const dayHolidays = getHolidaysForDay(holidays, day);
              
              // Use filtered appointments instead of all appointments
              const dayAppointments = sortAppointmentsByTime(
  filteredAppointments.filter(apt => 
    AppointmentServiceUtils.doesAppointmentSpanDate(apt, day)
  )
);
              
              return (
                <div 
                  key={index} 
                  className={`week-day-column ${isToday ? 'today' : ''} ${dayHolidays.length > 0 ? 'has-holiday' : ''} ${isMobile && selectedStylist ? 'mobile-filtered' : ''}`}
                >
                  <div className="day-header">
                    <div className="day-name">
                      {day.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className="day-number">{day.getDate()}</div>
                    {dayHolidays.length > 0 && (
                      <div 
                        className="holiday-indicator" 
                        title={dayHolidays.map(h => h.HolidayName).join(', ')}
                      >
                        🕎
                      </div>
                    )}
                    <div className="appointment-count">
                      {dayAppointments.length} appointment{dayAppointments.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                  
                  <div
                    className="day-appointments"
                    onClick={(e) => {
                      if (e.target === e.currentTarget) {
                        onDayClick(day);
                      }
                    }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, day)}
                  >
                    {dayAppointments.map((appointment) => (
<div
  key={appointment.ID}
  data-appointment-id={appointment.ID}
  className={`week-appointment ${getAppointmentTypeClass(appointment.Title)} ${
    isMobile && selectedStylist ? 'mobile-appointment' : ''
  } ${highlightedAppointment === appointment.ID ? 'highlighted' : ''}`} // Add this
  onClick={(e) => {
    e.stopPropagation();
    onAppointmentClick(appointment);
  }}
  draggable
  onDragStart={(e) => onDragStart(e, appointment)}
  onDragEnd={onDragEnd}
>
                        <div className="appointment-time">
                          {formatTime(appointment.EventStart, useMilitaryTime)}
                          {' - '}
                          {formatTime(appointment.EventEnd, useMilitaryTime)}
                        </div>
                        <div className="appointment-client">{appointment.ClientName}</div>
                        <div className="appointment-type">{appointment.Title}</div>
                        {/* Show stylist name only on desktop or when showing all stylists */}
                        {(!isMobile || !selectedStylist) && (
                          <div className="appointment-stylist">
                            with {appointment.MeetingWith}
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {dayAppointments.length === 0 && (
                      <div className="no-appointments">
                        <div className="add-appointment-prompt">
                          {isMobile && selectedStylist 
                            ? `No appointments for ${selectedStylist.name}`
                            : 'Click to add appointment'
                          }
                        </div>
                      </div>
                    )}
                    
                    <div className="week-add-appointment-btn">
                      <Plus className="w-4 h-4" />
                      <span>Add Appointment</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default WeekView;